import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';

export interface AppConfig {
  environment: string;
  environmentMessage?: string;
  configServiceUrl: string;
  tmsService: string;
  communicationsUrl?: string;
  jsReportServerUrl?: string;
  messageRefreshInterval?: number;
  useAccessDrivenMenu?: boolean;
  traceLoggingLevel?: number;
  serverLoggingLevel?: number;
  loggingServiceUrl?: string;
  encryptionKey?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    config?: AppConfig;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private readonly _isInitialized = signal(false);
  readonly isInitialized = this._isInitialized.asReadonly();
  private readonly configUrl = 'assets/config-data.json';

  constructor(
    private readonly httpClient: HttpClient,
    private readonly logger: NGXLogger
  ) { }

  load(): Promise<void> {
    if (this._isInitialized()) {
      return Promise.resolve();
    }

    return firstValueFrom(this.httpClient.get<AppConfig>(this.configUrl))
      .then((config) => {
        window.config = config;
        this.configureLogger(config);
        this._isInitialized.set(true);
      })
      .catch((error) => {
        console.error('Failed to load application configuration.', error);
        throw error;
      });
  }

  get config(): AppConfig | undefined {
    return window.config;
  }

  private configureLogger(config: AppConfig): void {
    const serverLogLevel = this.mapServerLogLevel(config);
    const serverLoggingUrl = serverLogLevel !== NgxLoggerLevel.OFF
      ? this.getLoggingServiceUrl(config)
      : undefined;

    this.logger.partialUpdateConfig({
      serverLoggingUrl,
      serverLogLevel,
      serverCallsBatchSize: 1,
    });
  }

  private getLoggingServiceUrl(config: AppConfig): string | undefined {
    if (config.loggingServiceUrl) {
      return config.loggingServiceUrl;
    }

    if (config.configServiceUrl) {
      return `${config.configServiceUrl}/client-log`;
    }

    return undefined;
  }

  private mapServerLogLevel(config: AppConfig): NgxLoggerLevel {
    if (typeof config.serverLoggingLevel === 'number') {
      switch (config.serverLoggingLevel) {
        case 0:
          return NgxLoggerLevel.OFF;
        case 1:
          return NgxLoggerLevel.DEBUG;
        case 2:
          return NgxLoggerLevel.INFO;
        case 3:
          return NgxLoggerLevel.WARN;
        case 4:
          return NgxLoggerLevel.ERROR;
        case 5:
          return NgxLoggerLevel.FATAL;
        default:
          return NgxLoggerLevel.ERROR;
      }
    }

    return this.mapLegacyTraceLogLevel(config.traceLoggingLevel);
  }

  private mapLegacyTraceLogLevel(traceLoggingLevel?: number): NgxLoggerLevel {
    switch (traceLoggingLevel) {
      case 5:
        return NgxLoggerLevel.TRACE;
      case 4:
        return NgxLoggerLevel.DEBUG;
      case 3:
        return NgxLoggerLevel.INFO;
      case 2:
        return NgxLoggerLevel.WARN;
      case 1:
      default:
        return NgxLoggerLevel.ERROR;
    }
  }
}
